/**
 * SentinelTimeBar — pick a Sentinel-2 acquisition for the current view.
 *
 * Purely a timeline: step through dates, play them, switch rendering. Anything
 * about detected land change lives under the Land Change layer, not here — this
 * bar answers "what did the ground look like on this day".
 *
 * Ticks are the actual acquisitions over this viewport, not a continuous axis,
 * because a date with no overpass here renders a transparent tile. Each tick is
 * coloured by cloud cover so the usable dates are obvious at a glance.
 */

import React, { useCallback, useEffect, useMemo, useRef } from 'react';
import { DATE_RANGES } from './useSentinelDates';
import { SCAN_SENSORS } from './sentinelSource';
import { describeAge } from './imageryPrefs';
import { useRightInset } from './useRightInset';

const CLOUD_CHOICES = [
  { value: 5, label: '≤ 5%' },
  { value: 20, label: '≤ 20%' },
  { value: 40, label: '≤ 40%' },
  { value: 101, label: 'Any' },
];

const PLAY_INTERVAL_MS = 1400;

const styles = {
  bar: {
    position: 'absolute', left: 12, bottom: 34, zIndex: 3,
    background: 'rgba(255,255,255,0.96)', borderRadius: 10,
    boxShadow: '0 2px 12px rgba(0,0,0,0.22)', padding: '10px 14px 8px',
    font: '13px system-ui, sans-serif', pointerEvents: 'auto',
  },
  top: { display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', marginBottom: 8 },
  date: { fontWeight: 600, fontSize: 15, whiteSpace: 'nowrap' },
  meta: { color: '#6b6b6b', fontSize: 12, whiteSpace: 'nowrap' },
  spacer: { flex: 1, minWidth: 8 },
  btn: {
    border: '1px solid #d6d0c9', background: '#fff', borderRadius: 6,
    padding: '3px 9px', cursor: 'pointer', font: 'inherit', lineHeight: 1.5,
  },
  btnOn: { background: '#fd7218', borderColor: '#fd7218', color: '#fff' },
  select: {
    border: '1px solid #d6d0c9', borderRadius: 6, padding: '3px 6px',
    font: 'inherit', background: '#fff', cursor: 'pointer',
  },
  track: {
    position: 'relative', height: 30, cursor: 'pointer',
    borderBottom: '1px solid #d6d0c9', marginBottom: 3,
  },
  axis: { display: 'flex', justifyContent: 'space-between', color: '#8a8a8a', fontSize: 11 },
  note: { color: '#8a5b06', fontSize: 12 },
};

function tickColour(cloud, selected) {
  if (selected) return '#1b1613';
  if (cloud === null || cloud === undefined) return '#b9b0a6';
  if (cloud <= 5) return '#fd7218';
  if (cloud <= 20) return '#f0a35e';
  if (cloud <= 40) return '#cfc4b8';
  return '#e2ded8';
}

export function SentinelTimeBar({
  dates, loading, selectedDate, onSelectDate,
  styleId, onStyleChange,
  sensor, onSensorChange,
  maxCloud, onMaxCloudChange,
  rangeId, onRangeChange,
  snapNote, playing, onPlayingChange,
}) {
  const trackRef = useRef(null);
  const rightInset = useRightInset();

  // Oldest → newest for drawing; the hook hands them back newest first.
  const ordered = useMemo(() => [...dates].reverse(), [dates]);
  const visible = useMemo(
    () => ordered.filter(d => d.cloud === null || d.cloud <= maxCloud),
    [ordered, maxCloud]
  );

  const span = useMemo(() => {
    if (!ordered.length) return null;
    const first = new Date(ordered[0].date).getTime();
    const last = new Date(ordered[ordered.length - 1].date).getTime();
    return { first, last, width: Math.max(1, last - first) };
  }, [ordered]);

  const selected = useMemo(() => dates.find(d => d.date === selectedDate) || null,
    [dates, selectedDate]);
  const age = selected ? describeAge(selected.datetime) : null;

  const positionOf = useCallback(
    d => (span ? ((new Date(d.date).getTime() - span.first) / span.width) * 100 : 0),
    [span]
  );

  const step = useCallback(
    delta => {
      if (!visible.length) return;
      const i = visible.findIndex(d => d.date === selectedDate);
      const next = i === -1 ? visible.length - 1 : Math.min(visible.length - 1, Math.max(0, i + delta));
      onSelectDate(visible[next].date);
    },
    [visible, selectedDate, onSelectDate]
  );

  // Play walks forward through clear dates and stops at the newest rather than
  // looping — reaching "now" is the end of the story, not a wrap-around.
  useEffect(() => {
    if (!playing) return undefined;
    const timer = setInterval(() => {
      const i = visible.findIndex(d => d.date === selectedDate);
      if (i === -1 || i >= visible.length - 1) { onPlayingChange(false); return; }
      onSelectDate(visible[i + 1].date);
    }, PLAY_INTERVAL_MS);
    return () => clearInterval(timer);
  }, [playing, visible, selectedDate, onSelectDate, onPlayingChange]);

  const pickNearest = useCallback(
    event => {
      if (!span || !visible.length) return;
      const rect = trackRef.current.getBoundingClientRect();
      const frac = Math.min(1, Math.max(0, (event.clientX - rect.left) / rect.width));
      const target = span.first + frac * span.width;
      let best = visible[0];
      let bestGap = Infinity;
      for (const d of visible) {
        const gap = Math.abs(new Date(d.date).getTime() - target);
        if (gap < bestGap) { bestGap = gap; best = d; }
      }
      onSelectDate(best.date);
    },
    [span, visible, onSelectDate]
  );

  const fmt = iso =>
    new Date(iso).toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' });

  return (
    <div style={{ ...styles.bar, right: rightInset + 12 }} className='sentinel-time-bar'>
      <div style={styles.top}>
        <button type='button' style={styles.btn} onClick={() => step(-1)} title='Previous acquisition'>◀</button>
        <button type='button' style={styles.btn} onClick={() => step(1)} title='Next acquisition'>▶</button>
        <button
          type='button'
          style={{ ...styles.btn, ...(playing ? styles.btnOn : {}) }}
          onClick={() => onPlayingChange(!playing)}
          title='Step forward through the clear dates'
        >
          {playing ? '❚❚ stop' : '▶ play'}
        </button>

        <span style={styles.date}>
          {selected ? fmt(selected.date) : loading ? 'Loading…' : 'No imagery here'}
        </span>
        {selected && (
          <span style={styles.meta}>
            {selected.cloud === null ? 'cloud unknown' : `${Math.round(selected.cloud)}% cloud`}
            {age ? ` · ${age.text}` : ''}
          </span>
        )}

        <span style={styles.spacer} />
        {snapNote && <span style={styles.note}>{snapNote}</span>}

        <button type='button' style={styles.btn} onClick={() => onSelectDate(null)} title='Jump to the newest clear scene'>
          ⤒ latest
        </button>
        <select
          style={styles.select}
          value={sensor?.id || 's2'}
          onChange={e => onSensorChange(e.target.value)}
          title='Sentinel-2 is 10 m back to 2015; Landsat is 30 m back to 1986'
        >
          {Object.values(SCAN_SENSORS).map(s => (
            <option key={s.id} value={s.id}>{s.label} · {s.resolution}</option>
          ))}
        </select>
        <select style={styles.select} value={styleId} onChange={e => onStyleChange(e.target.value)} title='Rendering'>
          {(sensor?.styles || SCAN_SENSORS.s2.styles).map(s => (
            <option key={s.id} value={s.id}>{s.label}</option>
          ))}
        </select>
        <select style={styles.select} value={maxCloud} onChange={e => onMaxCloudChange(Number(e.target.value))} title='Cloud filter'>
          {CLOUD_CHOICES.map(c => (
            <option key={c.value} value={c.value}>Cloud {c.label}</option>
          ))}
        </select>
        <select style={styles.select} value={rangeId} onChange={e => onRangeChange(e.target.value)} title='How far back to list'>
          {DATE_RANGES.map(r => (
            <option key={r.id} value={r.id}>{r.label}</option>
          ))}
        </select>
      </div>

      <div style={styles.track} ref={trackRef} onClick={pickNearest}>
        {ordered.map(d => {
          const isSelected = d.date === selectedDate;
          const dim = !(d.cloud === null || d.cloud <= maxCloud);
          return (
            <span
              key={d.date}
              title={`${fmt(d.date)} — ${d.cloud === null ? 'cloud unknown' : `${Math.round(d.cloud)}% cloud`}`}
              style={{
                position: 'absolute', bottom: 0, left: `${positionOf(d)}%`,
                width: isSelected ? 3 : 2,
                height: isSelected ? 26 : dim ? 9 : 16,
                marginLeft: -1, borderRadius: 1,
                background: tickColour(d.cloud, isSelected),
                opacity: dim ? 0.5 : 1,
              }}
            />
          );
        })}
      </div>
      <div style={styles.axis}>
        <span>{ordered.length ? fmt(ordered[0].date) : ''}</span>
        <span>
          {loading
            ? 'loading acquisitions…'
            : `${visible.length} of ${ordered.length} acquisitions${dates.truncated ? ' (capped)' : ''}`}
        </span>
        <span>{ordered.length ? fmt(ordered[ordered.length - 1].date) : ''}</span>
      </div>
    </div>
  );
}
